import { useRef, useState } from 'react';

import { BpkButtonLink } from '@skyscanner/backpack-web/bpk-component-link';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import StatementPopover from './StatementPopOver';
import StatementSheet from './StatementSheet';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { HotelReviewSummary } from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './RatingStatement.module.scss';

import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import {
  ACTION_TYPE,
  CLICK_AREA,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoViewButtonClicked } from '@shared/utils-frontend/mini-events/hotels-action';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
  ratingProvider?: HotelReviewSummary['ratingProvider'];
};

const RatingStatement = ({ i18n, miniEventTracker, ratingProvider }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const targetRef = useRef(null);

  const labelKey =
    ratingProvider === 'RATING_PROVIDER_TRIPADVISOR'
      ? 'rating_based_on_tripadvisor'
      : 'rating_based_on_providers';

  const handleToggle = () => {
    setIsOpen((previous) => {
      const willOpen = !previous;
      if (willOpen) {
        miniEventTracker.trackHotelsAction(
          ACTION_TYPE.VIEW_BUTTON_CLICKED,
          buildAdditionalInfoViewButtonClicked(CLICK_AREA.RATING_STATEMENT),
        );
      }
      return willOpen;
    });
  };

  const link = (
    <BpkButtonLink
      onClick={handleToggle}
      className={cls('RatingStatement__link')}
    >
      {i18n.translate(labelKey)}
    </BpkButtonLink>
  );

  return (
    <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
      {(isMobile: boolean) =>
        isMobile ? (
          <>
            <div ref={targetRef}>{link}</div>
            <StatementSheet isOpen={isOpen} onClose={() => setIsOpen(false)} />
          </>
        ) : (
          <StatementPopover
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            target={<div ref={targetRef}>{link}</div>}
          />
        )
      }
    </BpkBreakpoint>
  );
};

export default withMiniEventTracker(withI18n(RatingStatement));
